import { Form, Formik, type FormikHelpers } from "formik";
import { Check, Download } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { importClientsCsv, previewImportCsv } from "@/features/clients/api";
import type { ImportableField, ImportPreview, ImportResult } from "@/features/clients/types";
import { listPractitioners, type PractitionerOption } from "@/features/practitioners/api";
import { useStepWizard } from "@/hooks/use-step-wizard";
import { ApiError } from "@/lib/api";
import { downloadTextFile } from "@/lib/download";

const MAX_FILE_BYTES = 1_000_000;
// Select can't hold an empty value, so "don't import this field" uses a sentinel.
const SKIP = "__skip__";

interface UploadFormValues {
  file: File | null;
  practitionerId: string;
}

// Client-side checks are UX only — the server re-validates the file and every row.
const uploadSchema = Yup.object({
  file: Yup.mixed<File>()
    .required("Choose a CSV file.")
    .test("csv", "Upload a .csv file.", (f) => !f || f.name.toLowerCase().endsWith(".csv"))
    .test("size", "File may be at most 1 MB.", (f) => !f || f.size <= MAX_FILE_BYTES),
  practitionerId: Yup.string().required("Choose a practitioner."),
});

/** Which client types a column's values contain — drives which identity fields
 * must be mapped. Mirrors the server's detection so a sample re-check is cheap. */
function detectTypes(rows: Record<string, string>[], column: string) {
  let individual = false;
  let business = false;
  for (const row of rows) {
    const value = (row[column] ?? "").trim().toLowerCase();
    if (value.startsWith("indiv")) individual = true;
    else if (value.startsWith("bus")) business = true;
  }
  return { individual, business };
}

/** Mask a sensitive sample value (SSN/EIN) to its last 4 digits. */
function maskSensitive(value: string) {
  const digits = value.replace(/\D/g, "");
  if (!digits) return value ? "••••" : "";
  return `•••• ${digits.slice(-4)}`;
}

interface ImportClientsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fires after an import creates at least one client, so the page refetches. */
  onImported: () => void;
}

/**
 * Three-step import wizard: Upload → Map fields → Import & report. Exact
 * header matches auto-map; the user can re-point or skip columns (required
 * fields can't be skipped); the last step shows created/failed counts with a
 * readable error list and a downloadable failed-rows CSV.
 */
export function ImportClientsDialog({ open, onOpenChange, onImported }: ImportClientsDialogProps) {
  const wizard = useStepWizard(3);
  const [practitioners, setPractitioners] = useState<PractitionerOption[]>([]);

  const [file, setFile] = useState<File | null>(null);
  const [practitionerId, setPractitionerId] = useState("");
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  useEffect(() => {
    if (!open) return;
    let active = true;
    listPractitioners()
      .then((list) => active && setPractitioners(list))
      .catch(() => active && setPractitioners([]));
    return () => {
      active = false;
    };
  }, [open]);

  function resetAll() {
    setFile(null);
    setPractitionerId("");
    setPreview(null);
    setMapping({});
    setResult(null);
    wizard.reset();
  }

  function handleOpenChange(next: boolean) {
    if (!next) resetAll();
    onOpenChange(next);
  }

  async function handleUpload(values: UploadFormValues, bag: FormikHelpers<UploadFormValues>) {
    try {
      const pv = await previewImportCsv(values.file as File);
      setFile(values.file);
      setPractitionerId(values.practitionerId);
      setPreview(pv);
      setMapping({ ...pv.suggestedMapping });
      wizard.next();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't read the CSV.");
    } finally {
      bag.setSubmitting(false);
    }
  }

  function setFieldColumn(fieldKey: string, column: string) {
    setMapping((prev) => {
      const next = { ...prev };
      if (column === SKIP) delete next[fieldKey];
      else next[fieldKey] = column;
      return next;
    });
  }

  // Required = always-fields plus the identity fields for the types the file
  // actually contains. Detection uses the full-file count from the server when
  // the Client Type column is still the auto-matched one, else the sample rows.
  function presentTypes() {
    if (!preview) return { individual: false, business: false };
    const column = mapping.client_type;
    if (!column) return { individual: false, business: false };
    if (column === preview.suggestedMapping.client_type) return preview.detectedTypes;
    return detectTypes(preview.sampleRows, column);
  }

  function isRequired(field: ImportableField) {
    const types = presentTypes();
    return (
      field.requirement === "always" ||
      (field.requirement === "individual" && types.individual) ||
      (field.requirement === "business" && types.business)
    );
  }

  const requiredUnmet =
    preview?.fields.filter((f) => isRequired(f) && !mapping[f.key]).map((f) => f.key) ?? [];
  const usedColumns = Object.values(mapping);
  const duplicateColumns = [...new Set(usedColumns.filter((c, i) => usedColumns.indexOf(c) !== i))];
  const canImport = preview != null && requiredUnmet.length === 0;

  async function handleImport() {
    if (!file || !canImport) return;
    setImporting(true);
    try {
      const res = await importClientsCsv(file, practitionerId, mapping);
      setResult(res);
      if (res.created > 0) {
        toast.success(`Imported ${res.created} client${res.created === 1 ? "" : "s"}.`);
        onImported();
      }
      wizard.next();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Import failed.");
    } finally {
      setImporting(false);
    }
  }

  function downloadFailedRows() {
    if (!result?.failedCsv) return;
    downloadTextFile("clients-import-errors.csv", result.failedCsv, "text/csv;charset=utf-8");
  }

  const labelFor = (key: string) =>
    preview?.fields.find((f) => f.key === key)?.label ??
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import Clients</DialogTitle>
          <DialogDescription>
            {wizard.step === 0 && "Step 1 of 3 — choose a CSV and the default practitioner."}
            {wizard.step === 1 &&
              "Step 2 of 3 — match your columns to client fields. Exact matches are mapped for you."}
            {wizard.step === 2 && "Step 3 of 3 — import results."}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1 — Upload */}
        {wizard.step === 0 && (
          <Formik<UploadFormValues>
            initialValues={{ file, practitionerId }}
            validationSchema={uploadSchema}
            onSubmit={handleUpload}
          >
            {({ values, errors, touched, isSubmitting, setFieldValue }) => (
              <Form className="grid gap-4">
                <div className="grid gap-1.5">
                  <Label htmlFor="import-file">CSV file</Label>
                  <Input
                    id="import-file"
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) =>
                      void setFieldValue("file", e.currentTarget.files?.[0] ?? null)
                    }
                  />
                  <p className="text-muted-foreground text-xs">
                    Any column headers work — you'll map them next. Up to 500 rows.
                  </p>
                  {touched.file && errors.file && (
                    <p className="text-destructive text-sm">{errors.file}</p>
                  )}
                </div>
                <div className="grid gap-1.5">
                  <Label>Default practitioner</Label>
                  <Select
                    value={values.practitionerId}
                    onValueChange={(v) => void setFieldValue("practitionerId", v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Assigned to every imported client" />
                    </SelectTrigger>
                    <SelectContent>
                      {practitioners.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {touched.practitionerId && errors.practitionerId && (
                    <p className="text-destructive text-sm">{errors.practitionerId}</p>
                  )}
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Reading…" : "Next"}
                  </Button>
                </DialogFooter>
              </Form>
            )}
          </Formik>
        )}

        {/* Step 2 — Map fields */}
        {wizard.step === 1 && preview && (
          <div className="grid gap-3">
            <div className="max-h-[22rem] overflow-y-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client field</TableHead>
                    <TableHead>CSV column</TableHead>
                    <TableHead>Sample</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.fields.map((field) => {
                    const required = isRequired(field);
                    const column = mapping[field.key];
                    const autoMatched =
                      !!column && column === preview.suggestedMapping[field.key];
                    const sample = column
                      ? preview.sampleRows
                          .map((r) => r[column] ?? "")
                          .filter((v) => v.trim())
                          .slice(0, 2)
                          .map((v) => (field.sensitive ? maskSensitive(v) : v))
                          .join(", ")
                      : "";
                    return (
                      <TableRow key={field.key}>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="font-medium">{field.label}</span>
                            {required ? (
                              <Badge variant="secondary">Required</Badge>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground">
                                Optional
                              </Badge>
                            )}
                            {autoMatched && (
                              <Badge
                                variant="outline"
                                className="border-success/30 bg-success/10 text-success"
                              >
                                <Check className="size-3" /> Matched
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={column ?? SKIP}
                            onValueChange={(v) => setFieldColumn(field.key, v)}
                          >
                            <SelectTrigger
                              className="w-full"
                              aria-label={`Column for ${field.label}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={SKIP} disabled={required}>
                                Don't import
                              </SelectItem>
                              {preview.columns.map((col) => (
                                <SelectItem key={col} value={col}>
                                  {col}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {column ? sample || "(empty)" : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {requiredUnmet.length > 0 && (
              <p className="text-destructive text-sm">
                Map every required field to continue: {requiredUnmet.map(labelFor).join(", ")}.
              </p>
            )}
            {duplicateColumns.length > 0 && (
              <p className="text-warning text-sm">
                Heads up: {duplicateColumns.join(", ")} is mapped to more than one field.
              </p>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={wizard.back}>
                Back
              </Button>
              <Button type="button" onClick={handleImport} disabled={!canImport || importing}>
                {importing ? "Importing…" : `Import ${preview.rowCount} rows`}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step 3 — Results */}
        {wizard.step === 2 && result && (
          <div className="grid gap-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium">
                Imported {result.created} of {result.rowCount}.
              </span>
              <Badge variant="outline" className="border-success/30 bg-success/10 text-success">
                {result.created} succeeded
              </Badge>
              {result.errors.length > 0 && (
                <Badge variant="destructive">{result.errors.length} failed</Badge>
              )}
            </div>

            {result.errors.length > 0 && (
              <>
                <ul className="max-h-56 space-y-1 overflow-y-auto rounded-md border p-3 text-sm">
                  {result.errors.map((rowError) => (
                    <li key={rowError.row} className="text-destructive">
                      Row {rowError.row} —{" "}
                      {Object.entries(rowError.errors)
                        .map(([field, messages]) => `${labelFor(field)}: ${messages.join(" ")}`)
                        .join("; ")}
                    </li>
                  ))}
                </ul>
                <Button
                  type="button"
                  variant="outline"
                  className="w-fit"
                  onClick={downloadFailedRows}
                >
                  <Download className="size-4" /> Download failed rows
                </Button>
              </>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={resetAll}>
                Import Another
              </Button>
              <Button type="button" onClick={() => handleOpenChange(false)}>
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
